import { Link } from 'react-router-dom';
import { ShieldCheck, RefreshCw, Headphones, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const CTASection = () => {
  const trustIndicators = [
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: 'Secure Payments',
      subtitle: '100% Safe',
    },
    {
      icon: <RefreshCw className="w-6 h-6" />,
      title: 'Easy Returns',
      subtitle: '15-Day Policy',
    },
    {
      icon: <Headphones className="w-6 h-6" />,
      title: 'Customer Support',
      subtitle: '24/7 Available',
    },
  ];

  return (
    <section className="relative py-20 md:py-24 overflow-hidden bg-gradient-to-br from-cocoa-900 via-clay-700 to-saffron-600">
      {/* Decorative blobs */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-saffron-400/15 rounded-full blur-3xl animate-pulse-soft"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-rose-400/15 rounded-full blur-3xl animate-pulse-soft"></div>
      <div className="absolute inset-0 opacity-[0.06]">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22><path d=%22M20 20h60v60H20z%22 fill=%22none%22 stroke=%22white%22 stroke-width=%220.5%22/></svg>')]"></div>
      </div>

      <div className="relative container mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
          className="max-w-4xl mx-auto text-center"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/25 text-saffron-100 rounded-full text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            Join the Movement
          </span>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Support Traditional Craftsmanship?
          </h2>

          <p className="text-xl text-cream-100/90 mb-10 max-w-2xl mx-auto">
            Every purchase helps preserve Bengali art and supports local artisans
          </p>

          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Link
              to="/register"
              className="group inline-flex items-center justify-center bg-white text-clay-700 px-8 py-4 rounded-full font-bold text-lg hover:bg-cream-100 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
            >
              Join Our Community
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              to="/about"
              className="border-2 border-white/70 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/10 transition-all duration-300"
            >
              Learn About Our Mission
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
            {trustIndicators.map((indicator, index) => (
              <motion.div
                key={indicator.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-4"
              >
                <div className="text-saffron-200">{indicator.icon}</div>
                <div className="text-left">
                  <div className="font-bold text-white">{indicator.title}</div>
                  <div className="text-sm text-cream-100/80">{indicator.subtitle}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
